import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { Users, User, Edit, Check, X, Plus, Trash, Upload } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Player from '../models/Player';

const TeamManagementPage = () => {
  const navigate = useNavigate();
  const { teams, updateTeam, tournament } = useAppContext();
  const [activeTeamId, setActiveTeamId] = useState(teams.length > 0 ? teams[0].id : null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [newPlayer, setNewPlayer] = useState({
    name: '',
    number: '',
    position: 'FWD'
  });
  const [showBatchImport, setShowBatchImport] = useState(false);
  const [batchPlayerNames, setBatchPlayerNames] = useState('');

  if (!tournament) {
    navigate('/');
    return null;
  }

  const activeTeam = teams.find(team => team.id === activeTeamId) || teams[0];

  // Save player edit
  const savePlayerEdit = () => {
    if (!editingPlayer?.id || !activeTeam) return;

    const updatedPlayers = activeTeam.players.map(player =>
      player.id === editingPlayer.id ? { ...player, ...editingPlayer } : player
    );

    updateTeam(activeTeam.id, { players: updatedPlayers });
    setEditingPlayer(null);
  };

  // Cancel player edit
  const cancelPlayerEdit = () => {
    setEditingPlayer(null);
  };

  // Add new player
  const addNewPlayer = () => {
    if (!activeTeam || !newPlayer.name) return;

    const player = new Player({
      name: newPlayer.name,
      number: parseInt(newPlayer.number) || 0,
      position: newPlayer.position
    });

    updateTeam(activeTeam.id, {
      players: [...activeTeam.players, player]
    });

    setNewPlayer({
      name: '',
      number: '',
      position: 'FWD'
    });
  };

  // Batch import players
  const importBatchPlayers = () => {
    if (!activeTeam || !batchPlayerNames.trim()) return;

    // Split the text by newlines
    const playerNames = batchPlayerNames
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);

    if (playerNames.length === 0) return;

    // Create new player objects for each name
    const newPlayers = playerNames.map((name, index) => {
      return new Player({
        name,
        number: activeTeam.players.length + index + 1, // Auto-assign sequential numbers
        position: 'FWD' // Default position
      });
    });

    // Add all new players to the team
    updateTeam(activeTeam.id, {
      players: [...activeTeam.players, ...newPlayers]
    });

    // Reset batch import
    setBatchPlayerNames('');
    setShowBatchImport(false);
  };

  // Remove player
  const removePlayer = (playerId) => {
    if (!activeTeam) return;

    const updatedPlayers = activeTeam.players.filter(player => player.id !== playerId);
    updateTeam(activeTeam.id, { players: updatedPlayers });
  };

  // Get position label
  const getPositionLabel = (positionCode) => {
    switch (positionCode) {
      case 'GK': return 'Goalkeeper';
      case 'DEF': return 'Defender';
      case 'MID': return 'Midfielder';
      case 'FWD': return 'Forward';
      default: return positionCode;
    }
  };

  return (
    <div className="px-4 py-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <Users size={24} className="mr-2" />
        Team Management
      </h1>

      {/* Team Selection */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-3 -mx-4 px-4 mb-4">
        {teams.map(team => (
          <button
            key={team.id}
            className={`flex-shrink-0 px-4 py-2 rounded-full border ${
              team.id === activeTeamId
                ? 'border-blue-500 bg-blue-500 text-white'
                : 'border-gray-300 dark:border-gray-700'
            }`}
            onClick={() => setActiveTeamId(team.id)}
          >
            <div className="flex items-center">
              <div
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: team.id === activeTeamId ? 'white' : team.color }}
              />
              {team.name}
            </div>
          </button>
        ))}
      </div>

      {activeTeam && (
        <>
          <Card className="mb-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold flex items-center">
                <div
                  className="w-4 h-4 rounded-full mr-2"
                  style={{ backgroundColor: activeTeam.color }}
                />
                {activeTeam.name}
              </h2>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {tournament.format} • {activeTeam.players.length} players
              </div>
            </div>
          </Card>

          {/* Player List */}
          <Card className="mb-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <User size={20} className="mr-1" />
                Players
              </div>
              <Button
                small
                icon={<Upload size={16} />}
                onClick={() => setShowBatchImport(!showBatchImport)}
              >
                Batch Import
              </Button>
            </h2>

            {/* Batch import form */}
            {showBatchImport && (
              <div className="mb-4 p-3 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                <h3 className="text-sm font-medium mb-2">Paste player names (one per line)</h3>
                <textarea
                  value={batchPlayerNames}
                  onChange={(e) => setBatchPlayerNames(e.target.value)}
                  placeholder="John Doe
Jane Smith
Tom Johnson"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 mb-3 min-h-24"
                />
                <div className="flex justify-end space-x-2">
                  <Button
                    small
                    secondary
                    onClick={() => {
                      setBatchPlayerNames('');
                      setShowBatchImport(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    small
                    primary
                    onClick={importBatchPlayers}
                  >
                    Import Players
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {activeTeam.players.map(player => (
                <div key={player.id} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-0 last:pb-0">
                  {editingPlayer?.id === player.id ? (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editingPlayer.name}
                          onChange={(e) => setEditingPlayer({ ...editingPlayer, name: e.target.value })}
                          placeholder="Player name"
                          className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                        />
                        <input
                          type="number"
                          value={editingPlayer.number}
                          onChange={(e) => setEditingPlayer({ ...editingPlayer, number: e.target.value })}
                          placeholder="#"
                          className="w-16 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <select
                          value={editingPlayer.position}
                          onChange={(e) => setEditingPlayer({ ...editingPlayer, position: e.target.value })}
                          className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                        >
                          <option value="GK">Goalkeeper</option>
                          <option value="DEF">Defender</option>
                          <option value="MID">Midfielder</option>
                          <option value="FWD">Forward</option>
                        </select>

                        <Button small icon={<Check size={16} />} onClick={savePlayerEdit} className="min-w-10">
                          Save
                        </Button>

                        <Button small secondary icon={<X size={16} />} onClick={cancelPlayerEdit} className="min-w-10">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-semibold mr-3">
                          {player.number || 0}
                        </div>
                        <div>
                          <div className="font-medium">{player.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {getPositionLabel(player.position)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingPlayer(player)}
                          className="p-1 text-gray-500 hover:text-blue-500"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => removePlayer(player.id)}
                          className="p-1 text-gray-500 hover:text-red-500"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add new player */}
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium mb-2">Add New Player</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newPlayer.name}
                    onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })}
                    placeholder="Player name"
                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                  />
                  <input
                    type="number"
                    value={newPlayer.number}
                    onChange={(e) => setNewPlayer({ ...newPlayer, number: e.target.value })}
                    placeholder="#"
                    className="w-16 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <select
                    value={newPlayer.position}
                    onChange={(e) => setNewPlayer({ ...newPlayer, position: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                  >
                    <option value="GK">Goalkeeper</option>
                    <option value="DEF">Defender</option>
                    <option value="MID">Midfielder</option>
                    <option value="FWD">Forward</option>
                  </select>

                  <Button primary icon={<Plus size={16} />} onClick={addNewPlayer} disabled={!newPlayer.name}>
                    Add Player
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default TeamManagementPage;
